import {
  contractConvertToQuote,
  processingAddExistingExtraItem,
  processingAddStockItem,
  processingBasketSummary,
  processingContract,
  processingConvertDealerEnquiry,
  processingCreateJobLineItem,
  processingCreateJobLineItemFromDesigner,
  processingCreateTradeOrder,
  processingCreateTradeQuote,
  processingDeleteLineItem,
  processingDuplicateItem,
  processingForceDefaults,
  processingGetChildProducts,
  processingGetEarliestDeliveryDate,
  processingImage,
  processingJob,
  processingJobDelivery,
  processingJobLineItem,
  processingReloadContract,
  processingSaveContract,
  processingUpdateContract,
  processingUpdateCustomerPONumber,
  processingUpdateExistingExtraItem,
  processingUpdateFittingType,
  processingUpdateJob,
  processingUpdateJobDelivery,
  processingUpdateJobLineItemDimension,
  processingUpdateJobLineItemNestedDesign,
  processingUpdateJobLineItemOption,
  processingUpdateJobLineItemQuantity,
  processingUpdateJobLineStockItemQuantity,
  processingUpdateNewExtraItem,
  processingUpdateNote,
  reportDisplayStream,
  testCreateTestJob
} from '@/api'
import { FittingType, InputType, NoteType, SalesSectorType } from '@/enums'
import { useAuthStore } from '@/pinia/auth'
import { useCustomerStore } from '@/pinia/customer'
import { useSandboxStore } from '@/pinia/sandbox'
import { useTouchStore } from '@/pinia/touch'
import { useVisualiserStore } from '@/pinia/visualiser'
import alertbox from '@/utils/alertbox'
import { defineStore } from 'pinia'

export const useBasketStore = defineStore('basket', {
  persist: {
    omit: ['basket']
  },
  state: () => ({
    basket: [],
    customerId: undefined,
    salesSectorType: undefined,
    job: null,
    reference: '',
    deliveryNotes: '',
    supplierNotes: '',
    poNumber: '',
    deliveryDate: null,
    contractIdentifier: [],
    delivery: {},
    isEnquiry: false,
    isFit: true,
    isOrder: false,
    currentItem: undefined,
    contractData: undefined,
    imageType: null,
    basketQty: 0
  }),
  actions: {
    SET_IMAGE_TYPE(imageType) {
      this.imageType = imageType
    },
    setIsFit(isFit) {
      this.isFit = isFit
    },
    setContractData(contractData) {
      this.contractData = contractData
    },
    SET_CUSTOMER_ID(value) {
      this.customerId = value
    },
    SET_SALES_SECTOR_TYPE(value) {
      this.salesSectorType = value
    },
    SET_CURRENT_ITEM(value) {
      this.currentItem = value
    },
    APPEND_BASKET_ITEM(item) {
      this.basket.push({
        ...item,
        key: item.key ?? item.itemKey,
        _uuid: new Date().getTime()
      })
      this.setContract({ ...this.getContractIdentifier, basketQty: this.getBasketQty })
    },
    REPLACE_BASKET_ITEM({ itemKey, item }) {
      const itemIndex = this.basket.findIndex((basketItem) => basketItem.itemKey === Number(itemKey))
      this.basket[itemIndex] = {
        ...item,
        key: item.key ?? item.itemKey,
        _uuid: new Date().getTime()
      }
      this.setContract({ ...this.getContractIdentifier, basketQty: this.getBasketQty })
    },
    DELETE_BASKET_ITEM(itemKey) {
      this.basket = this.basket.filter((basketItem) => basketItem.itemKey !== Number(itemKey))
      this.setContract({ ...this.getContractIdentifier, basketQty: this.getBasketQty })
    },
    setDeliveryDate(value) {
      this.deliveryDate = value
    },
    setPONumber(value) {
      this.poNumber = value
    },
    setDeliveryNotes(value) {
      this.deliveryNotes = value
    },
    setSupplierNotes(value) {
      this.supplierNotes = value
    },
    saveJobDelivery(value) {
      this.delivery = value
    },
    setReference(value) {
      this.reference = value
    },
    clearBasket() {
      const authStore = useAuthStore()
      this.basket = []
      this.reference = ''
      this.job = undefined
      this.contractIdentifier = Array.isArray(this.contractIdentifier)
        ? this.contractIdentifier.filter((x) => x.contactId !== authStore.id)
        : []
      this.isEnquiry = false
      this.delivery = {}
      this.currentItem = undefined
      this.contractData = undefined
    },
    setBasket(items) {
      // Sort child items in order
      items.sort((a, b) => {
        return a.parentItemKey - b.parentItemKey
      })
      // Get all item level extras in reverse order
      const extraItems = items.filter((x) => x.extraItemId && x.parentItemKey !== 0)
      for (let i = extraItems.length - 1; i >= 0; i--) {
        // Remove from current location
        const extraIndexOld = items.findIndex((item) => item.itemKey === extraItems[i].itemKey)
        items.splice(extraIndexOld, 1)
        // Add above first parent element
        const extraIndexNew = items.findIndex((item) => item.itemKey === extraItems[i].parentItemKey)
        items.splice(extraIndexNew + 1, 0, extraItems[i])
      }
      // Sort all job extras to the bottom
      items.sort((a, b) => {
        const jobExtraA = a.parentItemKey === 0 && a.extraItemId
        const jobExtraB = b.parentItemKey === 0 && b.extraItemId
        // If jobExtraA move down
        if (jobExtraA && !jobExtraB) return 1
        // If jobExtraB move A up
        if (!jobExtraA && jobExtraB) return -1
      })
      this.basket = items
    },
    setJob(job) {
      this.job = job
    },
    setIsEnquiry(value) {
      this.isEnquiry = value
    },
    setContract(contract) {
      const authStore = useAuthStore()

      if (!Array.isArray(this.contractIdentifier)) {
        this.contractIdentifier = []
      }

      const index = this.contractIdentifier.findIndex((x) => x.contactId === authStore.id)
      if (index !== -1) {
        this.contractIdentifier[index] = { ...contract, contactId: authStore.id }
      } else {
        this.contractIdentifier.push({ ...contract, contactId: authStore.id })
      }
      this.isEnquiry = !!contract.isEnquiry
      this.isOrder = !!contract.isOrder
    },
    async getSandboxReportUrlForCurrentItem() {
      const sandboxStore = useSandboxStore()
      const customerStore = useCustomerStore()
      const sandboxId = sandboxStore.selectedSandbox?.id || 0
      const { contractId, jobKey } = this.getContractIdentifier
      const { itemKey } = this.currentItem
      const customerId = this.customerId || customerStore?.customer?.customerId

      let url =
        (sandboxId ? `v1/sandbox/${sandboxId}/` : '') +
        `processing/GenerateFromAdminProcessing/${customerId}/${contractId}/${jobKey}/${itemKey}`

      return reportDisplayStream(url, { type: 'application/pdf' })
    },
    async updateStockItemQty({ itemId, quantity }) {
      const existingItem = this.basket.find(
        (basketItem) => basketItem.inputType === InputType.STOCK_ITEM && basketItem.stockItemId === itemId
      )
      const contract = await this.getContract()

      if (!existingItem) {
        const addedStockItem = await processingAddStockItem(contract.contractId, contract.jobKey, itemId, quantity, 0)
        await this.appendItemByKey(addedStockItem.itemKey)
        return addedStockItem.itemKey
      }

      const lineItem = await this.updateStockItemQuantity({
        itemKey: existingItem.itemKey,
        qty: quantity
      })

      this.REPLACE_BASKET_ITEM({
        itemKey: existingItem.itemKey,
        item: lineItem
      })

      this.refresh()

      return lineItem
    },
    async setFit(isFit) {
      this.setIsFit(isFit)

      const contract = await this.getContract()

      await processingUpdateFittingType(contract.contractId, contract.jobKey, isFit)

      return this.refresh()
    },
    async updateExistingExtraItem({ params, itemId }) {
      const contract = await this.getContract()

      await processingUpdateExistingExtraItem(contract.contractId, contract.jobKey, itemId, params)

      return this.refreshItemByKey(itemId)
    },
    async updateNewExtraItem({ params, itemId }) {
      const contract = await this.getContract()

      await processingUpdateNewExtraItem(contract.contractId, contract.jobKey, itemId, params)

      return this.refreshItemByKey(itemId)
    },
    async addExistingExtraItem({ itemId, extraItemId, params }) {
      const contract = await this.getContract()

      const result = await processingAddExistingExtraItem(
        contract.contractId,
        contract.jobKey,
        extraItemId,
        itemId,
        params
      )

      if (result) {
        return this.appendItemByKey(result.itemKey)
      }
    },
    async markAsMeasured() {
      return processingUpdateContract(this.getContractIdentifier.contractId, {
        NoSizes: false
      })
    },
    async setMeasurements(measurementsOn) {
      await processingUpdateContract(this.getContractIdentifier.contractId, {
        NoSizes: !measurementsOn
      })

      return this.refresh()
    },
    async setCustomerId(customerId) {
      if (customerId === undefined) {
        return
      }

      if (Number.isNaN(Number(customerId))) {
        return
      }
      this.SET_CUSTOMER_ID(customerId)
    },
    async setSalesSectorType(salesSectorType) {
      if (salesSectorType === undefined) {
        return
      }
      this.SET_SALES_SECTOR_TYPE(salesSectorType)
    },
    async createTest(isEnquiry = false) {
      this.clearBasket()

      const quote = await testCreateTestJob(this.customerId)

      if (quote.message) {
        await alertbox(quote.message)
        return false
      }

      this.setContract({
        contractId: quote.contractId,
        jobKey: quote.jobKey,
        isEnquiry,
        contractType: 'Test Job'
      })

      return true
    },
    async createQuote(isEnquiry = false) {
      const sandboxStore = useSandboxStore()
      sandboxStore.SET_SANDBOX(null)
      this.clearBasket()

      const quote = await processingCreateTradeQuote(this.customerId)

      if (quote) {
        this.setContract({
          contractId: quote.contractId,
          jobKey: quote.jobKey,
          isEnquiry,
          contractType: 'Quote',
          isInProcessStore: true
        })

        return true
      } else {
        return false
      }
    },
    async createOrder() {
      const sandboxStore = useSandboxStore()
      sandboxStore.SET_SANDBOX(null)
      this.clearBasket()

      const order = await processingCreateTradeOrder(this.customerId)

      if (order) {
        this.setContract({
          contractId: order.contractId,
          jobKey: order.jobKey,
          isOrder: true,
          contractType: 'Order',
          isInProcessStore: true
        })

        return true
      } else {
        return false
      }
    },
    async fromQuote(contract) {
      const sandboxStore = useSandboxStore()
      sandboxStore.SET_SANDBOX(null)
      await processingReloadContract(this.customerId, contract.contractId)
      this.setContract({
        contractId: contract.contractId,
        jobKey: contract.jobKey,
        isEnquiry: contract.isEnquiry,
        contractType: 'Quote'
      })
    },
    async fromOrder(contract) {
      const sandboxStore = useSandboxStore()
      sandboxStore.SET_SANDBOX(null)
      await processingReloadContract(this.customerId, contract.contractId)
      this.setContract({
        contractId: contract.contractId,
        jobKey: contract.jobKey,
        isOrder: true,
        existsInBusinessStore: true,
        contractType: 'Order'
      })
    },
    async fromEnquiry(contract) {
      const authStore = useAuthStore()
      let contractId

      if (contract.fromDealer) {
        contractId = await processingConvertDealerEnquiry(
          contract.contractId,
          [contract.jobKey],
          authStore.processingLevel
        )
      } else {
        await processingReloadContract(this.customerId, contract.contractId)
        contractId = await contractConvertToQuote(contract.contractId, [contract.jobKey])
      }

      this.setContract({
        contractId,
        jobKey: 1,
        isEnquiry: false,
        contractType: 'Quote',
        isInProcessStore: true
      })

      await processingReloadContract(this.customerId, contractId)
    },
    async addDesign({ designId, productId, customerId = this.customerId }) {
      const contract = await this.getContract()
      const authStore = useAuthStore()

      if (this.contractData === undefined) {
        const contractData = await processingContract(contract.contractId)
        this.setContractData(contractData)
      }

      const addedDesign = await processingCreateJobLineItem(
        customerId,
        contract.contractId,
        contract.jobKey,
        designId,
        productId,
        authStore.processingLevel
      )

      if (addedDesign) {
        this.APPEND_BASKET_ITEM(addedDesign)
        return addedDesign.itemKey
      }
    },
    async addDesignFromDesigner(Designer) {
      const contract = await this.getContract()
      const authStore = useAuthStore()

      if (this.contractData === undefined) {
        const contractData = await processingContract(contract.contractId)
        this.setContractData(contractData)
      }

      const addedDesign = await processingCreateJobLineItemFromDesigner({
        CustomerId: this.customerId,
        ContractId: contract.contractId,
        JobKey: contract.jobKey,
        ProcessingLevel: authStore.processingLevel,
        Designer
      })

      if (addedDesign) {
        this.APPEND_BASKET_ITEM(addedDesign)
        return addedDesign.itemKey
      }
    },
    async deleteItemByKey(itemKey) {
      this.DELETE_BASKET_ITEM(itemKey)
    },
    async refreshItemByKey(itemKey) {
      const contract = await this.getContract()
      const authStore = useAuthStore()

      const basketItems = await processingJob(contract.contractId, contract.jobKey)

      if (basketItems === null || basketItems.items === undefined) {
        throw new Error('Unexpected Empty Contract')
      }

      const newItem = basketItems.items.find((item) => item.key === itemKey)

      const fullItem = await processingJobLineItem(
        this.customerId,
        contract.contractId,
        contract.jobKey,
        itemKey,
        authStore.processingLevel,
        false,
        false,
        basketItems.exchangeRate
      )

      this.REPLACE_BASKET_ITEM({
        itemKey,
        item: {
          ...newItem,
          ...fullItem
        }
      })
    },
    async appendItemByKey(itemKey) {
      const contract = await this.getContract()
      const authStore = useAuthStore()

      if (this.contractData === undefined) {
        const contractData = await processingContract(contract.contractId)
        this.setContractData(contractData)
      }

      const basketItems = await processingJob(contract.contractId, contract.jobKey)

      if (basketItems === null || basketItems.items === undefined) {
        throw new Error('Unexpected Empty Contract')
      }

      const newItem = basketItems.items.find((item) => item.key === itemKey)

      const fullItem = await processingJobLineItem(
        this.customerId,
        contract.contractId,
        contract.jobKey,
        itemKey,
        authStore.processingLevel,
        false,
        false,
        basketItems.exchangeRate
      )

      this.APPEND_BASKET_ITEM({
        ...newItem,
        ...fullItem
      })
    },
    async getChildProducts({ componentId, width, height, customerId }) {
      const contract = await this.getContract()

      const products = await processingGetChildProducts(
        contract.contractId,
        contract.jobKey,
        componentId,
        width,
        height,
        customerId
      )

      return products
    },
    async insertDesignIntoBay({ product, designId, itemId, componentId }) {
      const contract = await this.getContract()
      const authStore = useAuthStore()

      const response = await processingUpdateJobLineItemNestedDesign(
        this.customerId,
        contract.contractId,
        contract.jobKey,
        itemId,
        authStore.processingLevel,
        product,
        designId,
        componentId
      )

      await this.refreshItemByKey(itemId)

      return response
    },
    getComponentDimensions(componentId) {
      const container = this.currentItem.frameContainers.filter((frameContainer) => frameContainer.id === componentId)

      if (container.length !== 1) {
        throw new Error('Unable to find component')
      }

      return container[0]
    },
    async updateItemDimension({ itemKey, dimensionId, value }) {
      const contract = await this.getContract()
      const authStore = useAuthStore()

      const lineItem = await processingUpdateJobLineItemDimension(
        this.customerId,
        contract.contractId,
        contract.jobKey,
        itemKey,
        authStore.processingLevel,
        dimensionId,
        value
      )

      lineItem._uuid = new Date().getTime()

      if (lineItem) {
        this.REPLACE_BASKET_ITEM({
          itemKey: this.currentItem.itemKey,
          item: lineItem
        })

        await this.refresh()
        return lineItem
      }
    },
    async updateItemQty({ itemKey, qty }) {
      const contract = await this.getContract()
      const authStore = useAuthStore()

      const lineItem = await processingUpdateJobLineItemQuantity(
        this.customerId,
        contract.contractId,
        contract.jobKey,
        itemKey,
        authStore.processingLevel,
        qty
      )

      this.REPLACE_BASKET_ITEM({
        itemKey,
        item: lineItem
      })
      await this.refresh()
      return lineItem
    },
    async updateStockItemQuantity({ itemKey, qty }) {
      const contract = await this.getContract()
      const authStore = useAuthStore()

      return processingUpdateJobLineStockItemQuantity(
        contract.contractId,
        contract.jobKey,
        itemKey,
        authStore.processingLevel,
        qty
      )
    },
    async updateImageTypeForHeading(headingId) {
      const imageType = await this.imageTypeForHeading({ heading: headingId })

      if (imageType) {
        this.SET_IMAGE_TYPE(imageType)
      }
    },
    async imageTypeForHeading({ heading }) {
      return this.currentItem.specification?.filter((spec) => spec.optionHeadingId === heading)?.[0]?.drawingType
    },
    async updateItemOption({ itemKey, heading, value, components, members, text, rule, parameters }) {
      const contract = await this.getContract()
      const authStore = useAuthStore()

      const lineItem = await processingUpdateJobLineItemOption(
        this.customerId,
        contract.contractId,
        contract.jobKey,
        itemKey,
        authStore.processingLevel,
        heading,
        value,
        components,
        members,
        parameters,
        text,
        rule
      )

      lineItem._uuid = new Date().getTime()

      if (lineItem) {
        this.REPLACE_BASKET_ITEM({
          itemKey: this.currentItem.itemKey,
          item: lineItem
        })

        return lineItem
      }
    },
    async loadItem({ itemKey, customerID, recalculate = false }) {
      if (this.basket.length === 0) {
        await this.refresh()
      }
      if (this.basket.length > 0) {
        const item = this.basket.filter((x) => Number(x.key) === Number(itemKey))[0]
        const authStore = useAuthStore()

        if (!item) {
          return undefined
        }
        const fullItem = await processingJobLineItem(
          customerID,
          item.contractId,
          item.jobKey,
          item.itemKey,
          authStore.processingLevel,
          recalculate,
          false
        )
        return fullItem
      }
      return undefined
    },
    async getEarliestDeliveryDate() {
      const contract = await this.getContract()
      return processingGetEarliestDeliveryDate(contract.contractId, contract.jobKey)
    },
    async processingForceDefaults() {
      const contract = await this.getContract()
      const authStore = useAuthStore()

      await processingForceDefaults(contract.contractId, contract.jobKey, this.currentItem, authStore.processingLevel)
      return this.refreshItemByKey(this.currentItem)
    },
    async refresh() {
      const contract = await this.getContract()
      const authStore = useAuthStore()

      const contractData = await processingContract(contract.contractId)
      this.setContractData(contractData)

      await this.refreshDelivery()

      const uuid = new Date().getTime()
      const basketSummary = await processingBasketSummary(
        this.customerId,
        contract.contractId,
        contract.jobKey,
        authStore.processingLevel
      )
      if (basketSummary) {
        for (let i = 0; i < basketSummary.items.length; i++) {
          basketSummary.items[i].key = basketSummary.items[i].itemKey
          basketSummary.items[i]._uuid = uuid + 1
        }
        this.setJob({ ...basketSummary, items: undefined })
        this.setIsFit(basketSummary.fittingType === FittingType.SUPPLY_AND_FIT)
        this.setBasket(basketSummary.items)
        this.setContract({ ...this.getContractIdentifier, basketQty: this.getBasketQty })
      }
    },
    async saveBrandedEnquiry({
      salutation,
      firstName,
      lastName,
      email,
      phone,
      addressLine1,
      addressLine2,
      addressLine3,
      addressTown,
      addressCode,
      addressIso,
      notes,
      latitude,
      longitude
    }) {
      const contract = await this.getContract()
      // set quote reference
      const quoteId = contract.contractId

      this.clearBasket()

      await processingUpdateContract(contract.contractId, {
        Reference: this.reference,
        Consumer: {
          Salutation: salutation,
          FirstName: firstName,
          LastName: lastName,
          Email: email,
          Mobile: phone,
          AddressLine1: addressLine1,
          AddressLine2: addressLine2,
          AddressLine3: addressLine3,
          AddressTown: addressTown,
          AddressCode: addressCode,
          AddressIso: addressIso,
          AddressLatitude: latitude,
          AddressLongitude: longitude
        },
        OverwriteConsumer: true
      })

      await processingUpdateNote({
        ContractId: contract.contractId,
        JobKey: 1,
        NoteType: NoteType.CUSTOMER,
        Text: notes
      })

      await processingSaveContract(this.customerId, contract.contractId, false, false, false)
      const visualiserStore = useVisualiserStore()
      visualiserStore.clear({})

      return quoteId
    },
    async updateOrder({ isPlaceOrder }) {
      const contract = await this.getContract()
      // set quote reference

      await processingUpdateContract(contract.contractId, {
        Reference: this.reference
      })

      if (contract.jobKey === 1) {
        await processingUpdateJob(contract.contractId, contract.jobKey, {
          Reference: this.reference
        })
      }

      const orderId = contract.contractId

      if (!isPlaceOrder) {
        await processingSaveContract(this.customerId, contract.contractId, false, false)
        this.clearBasket()
      }

      return {
        orderId
      }
    },
    async saveQuote(isLazySaving = true) {
      const contract = await this.getContract()
      // set quote reference
      let quoteId

      if (this.isEnquiry) {
        quoteId = await contractConvertToQuote(contract.contractId, [contract.jobKey])
      } else {
        quoteId = contract.contractId
      }

      const jobId = this.isEnquiry ? 1 : contract.jobKey

      await processingUpdateContract(quoteId, {
        Reference: this.reference
      })

      if (jobId === 1) {
        await processingUpdateJob(quoteId, jobId, {
          Reference: this.reference
        })
      }

      await processingSaveContract(this.customerId, quoteId, false, false, isLazySaving)

      return {
        jobId,
        quoteId
      }
    },
    async saveOrder({ isLazySaving = true, placeTradeOrder = false, isRetailFlow = false }) {
      const contract = await this.getContract()
      const orderId = contract.contractId
      const authStore = useAuthStore()

      await processingUpdateContract(orderId, {
        Reference: this.reference
      })

      if (!authStore.isExtruderOrExpress) {
        if (isRetailFlow) {
          await this.refreshDelivery()
        } else {
          await processingUpdateJobDelivery(this.customerId, orderId, 1, {
            RequestedDate: this.deliveryDate === '' ? null : this.deliveryDate,
            Notes: this.deliveryNotes
          })
        }
      }

      let existingNotes
      if (contract.existsInBusinessStore) {
        const touchStore = useTouchStore()
        existingNotes = touchStore.loadNote({
          contractId: orderId,
          jobId: 1,
          noteType: NoteType.SUPPLIER
        })
      }
      const existingSupplierNoteId = existingNotes?.id
      await processingUpdateNote({
        NoteId: existingSupplierNoteId,
        ContractId: orderId,
        JobKey: 1,
        NoteType: NoteType.SUPPLIER,
        Text: this.supplierNotes
      })

      await processingUpdateCustomerPONumber(orderId, this.poNumber)

      if (!this.isOrder) {
        await processingSaveContract(this.customerId, orderId, false, false, isLazySaving)
        return orderId
      }

      if (this.salesSectorType === SalesSectorType.RETAIL) {
        await processingSaveContract(this.customerId, orderId, false, placeTradeOrder, isLazySaving)
        return orderId
      }

      return processingSaveContract(this.customerId, orderId, false, placeTradeOrder, isLazySaving)
    },
    async addUserEnteredAddress({ town, address1, address2, address3, iso, postcode, latitude, longitude, county }) {
      const contract = await this.getContract()

      await processingUpdateJobDelivery(this.customerId, contract.contractId, contract.jobKey, {
        NewAddress: {
          Line1: address1,
          Line2: address2,
          Line3: address3,
          Town: town,
          Iso: iso,
          Code: postcode,
          County: county,
          Latitude: latitude,
          Longitude: longitude
        }
      })

      await this.refreshDelivery()
      await this.refresh()
    },
    async getContract() {
      if (!this.getContractIdentifier) {
        throw new Error('No Contract Setup')
      }

      if (!this.job) {
        this.setJob(
          await processingJob(this.getContractIdentifier.contractId, this.getContractIdentifier.jobKey, false)
        )
      }

      return {
        contractId: this.getContractIdentifier.contractId,
        jobKey: this.getContractIdentifier.jobKey,
        existsInBusinessStore: this.getContractIdentifier.existsInBusinessStore
      }
    },
    /*
      Path A 
        - Step 1: No Address Selected from processingJobDelivery
        - Step 2: Get Primary Address from all addresses
        - Step 3: Update delivery with selected address (setAddress, processingUpdateJobDelivery)
        - Step 4: Get delivery info from selected address (refreshDelivery, processingJobDelivery)
        - step 5: Save delivery info to store (saveJobDelivery)
      Path B
        - Step 1: Address Selected 
        - Step 2: Save delivery info to store (saveJobDelivery)
        - Step 3: Update delivery with selected address (setAddress, processingUpdateJobDelivery)
    */
    async refreshDelivery({ refresh = true } = {}) {
      const sandboxStore = useSandboxStore()
      if (this.isEnquiry || sandboxStore.sandboxIsActive) return
      const jobDelivery = await processingJobDelivery(
        this.customerId,
        this.getContractIdentifier.contractId,
        this.getContractIdentifier.jobKey
      )

      if (jobDelivery.addressId === 0) {
        const primaryAddress = jobDelivery.addresses.find((address) => address.isPrimary)
        if (primaryAddress) {
          await this.setAddress({ addressId: primaryAddress.id, refresh })
        }
      } else {
        this.saveJobDelivery(jobDelivery)
        if (refresh) {
          await this.setAddress({ addressId: jobDelivery.addressId, refresh: false })
        }
      }
    },
    async setAddress({ addressId, refresh = true }) {
      const contract = this.getContractIdentifier
      await processingUpdateJobDelivery(this.customerId, contract.contractId, contract.jobKey, {
        AddressId: addressId
      })
      this.setDeliveryDate('')
      if (refresh && addressId) {
        await this.refreshDelivery({ refresh: false })
      }
    },
    async deleteLineItem({ lineItemId }) {
      const contract = await this.getContract()
      const visualiserStore = useVisualiserStore()
      await processingDeleteLineItem(contract.contractId, contract.jobKey, lineItemId, 10)
      await visualiserStore.deleteProduct({ id: lineItemId })
      this.deleteItemByKey(lineItemId)
      await this.refresh()
    },
    async duplicateLineItem({ lineItemId }) {
      const contract = await this.getContract()
      const newItem = await processingDuplicateItem(
        this.customerId,
        contract.contractId,
        contract.jobKey,
        lineItemId,
        10
      )
      await this.refresh()

      return newItem
    },
    async getProcessingImage({ itemKey, imageType }) {
      const contract = await this.getContract()
      const image = await processingImage(contract.contractId, contract.jobKey, itemKey, imageType)
      return image
    }
  },
  getters: {
    getBasketQty() {
      return this.stockItems.length + this.jobLevelExtras.length + this.fenestrationItems.length
    },
    stockItems(state) {
      return state.basket.filter((item) => item.inputType === InputType.STOCK_ITEM).filter((item) => item)
    },
    jobLevelExtras(state) {
      return state.basket
        .filter((item) => item.inputType === InputType.CUSTOMER)
        .filter((item) => item)
        .filter((item) => item.parentItemKey === 0)
    },
    fenestrationItems(state) {
      return state.basket.filter((item) => item.inputType === InputType.FENESTRATION).filter((item) => item)
    },
    contractHasSizing(state) {
      return state.contractData && !state.contractData.noSizes
    },
    hasContract(state) {
      return state.getContractIdentifier !== null
    },
    getCurrency(state) {
      if (state.contractData === undefined) {
        return ''
      }

      return state.contractData.customerCurrencyCode
    },
    selected_address_id(state) {
      try {
        return state.delivery.addressId
      } catch {
        return -1
      }
    },
    available_addresses(state) {
      try {
        return state.delivery.addresses // .filter(address => address.isSelectable);
      } catch {
        return []
      }
    },
    total(state) {
      try {
        return state.job.priceIncludingTax
      } catch {
        return 0
      }
    },
    fittingPrice(state) {
      try {
        if (state.salesSectorType === SalesSectorType.RETAIL) {
          return state.job.fittingPriceIncTax
        }
        return state.job.fittingPrice
      } catch {
        return 0
      }
    },
    surveyPrice(state) {
      try {
        if (state.salesSectorType === SalesSectorType.RETAIL) {
          return state.job.surveyPriceIncTax
        }
        return state.job.surveyPrice
      } catch {
        return 0
      }
    },
    vat(state) {
      try {
        return state.job.tax
      } catch {
        return 0
      }
    },
    deliveryCharge(state) {
      try {
        if (state.salesSectorType === SalesSectorType.RETAIL) {
          return state.job.priceDeliveryChargeIncTax
        }
        return state.job.priceDeliveryCharge
      } catch {
        return 0
      }
    },
    subtotal(state) {
      try {
        return state.job.priceExcludingTax
      } catch {
        return 0
      }
    },
    getContractIdentifier(state) {
      const authStore = useAuthStore()
      if (Array.isArray(state.contractIdentifier)) {
        return state.contractIdentifier.find((x) => x.contactId === authStore.id) || null
      }
      return state.contractIdentifier
    }
  }
})
